// §4.14 gap-fill (FR-AD2): per-grackle content admin — org admin/owner can browse every
// recording in the org (not just their own), reassign its owner, or delete it. Reuses
// GET /orgs/{org_id}/grackles?filter=all (grackles.rs), which already includes every recording an
// org admin is authorized to see — org admin/owner sees everything regardless of visibility.
import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";
import { api, ApiFail, deleteErrorMessage } from "../api/client";
import type { GrackleOut, ListResponse, MemberOut, PagedResponse } from "../api/types";
import { Button, HoldBadge, Notice } from "../components/ui";
import type { AdminOutletContext } from "./AdminLayout";
import { Badge, Card, Select } from "@grackle/ui";

function visibilityLabel(v: string): string {
  return v.charAt(0).toUpperCase() + v.slice(1);
}

function statusLabel(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function ReassignControl({
  grackle,
  members,
  onDone,
}: {
  grackle: GrackleOut;
  members: MemberOut[];
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [ownerId, setOwnerId] = useState(grackle.owner.id);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit() {
    if (ownerId === grackle.owner.id) {
      setOpen(false);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api("PATCH", `/grackles/${grackle.id}/owner`, { owner_id: ownerId });
      setOpen(false);
      onDone();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't reassign that recording.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return (
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        Reassign
      </Button>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
      <div style={{ display: "flex", gap: 6 }}>
        <Select
          size="sm"
          aria-label="New owner"
          value={ownerId}
          onChange={setOwnerId}
          options={members.map((m) => ({ value: m.user.id, label: m.user.name || m.user.email }))}
        />
        <Button size="sm" onClick={onSubmit} loading={submitting}>
          Save
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
      {error ? <Notice tone="error">{error}</Notice> : null}
    </div>
  );
}

export default function ContentPage() {
  const { org } = useOutletContext<AdminOutletContext>();
  const [grackles, setGrackles] = useState<GrackleOut[]>([]);
  const [members, setMembers] = useState<MemberOut[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<GrackleOut | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [glms, mems] = await Promise.all([
        api<PagedResponse<GrackleOut>>("GET", `/orgs/${org.id}/grackles?filter=all&limit=50`),
        api<ListResponse<MemberOut>>("GET", `/orgs/${org.id}/members`),
      ]);
      setGrackles(glms.items);
      setCursor(glms.next_cursor);
      setMembers(mems.items);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't load recordings.");
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onLoadMore() {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const res = await api<PagedResponse<GrackleOut>>(
        "GET",
        `/orgs/${org.id}/grackles?filter=all&limit=50&cursor=${encodeURIComponent(cursor)}`,
      );
      setGrackles((prev) => [...prev, ...res.items]);
      setCursor(res.next_cursor);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't load more.");
    } finally {
      setLoadingMore(false);
    }
  }

  async function onDeleteConfirm() {
    if (!deleteConfirm) return;
    const { id } = deleteConfirm;
    setDeleteConfirm(null);
    setRowError(null);
    try {
      await api("DELETE", `/grackles/${id}`);
      setGrackles((prev) => prev.filter((g) => g.id !== id));
    } catch (err) {
      setRowError(deleteErrorMessage(err, "Couldn't delete that recording."));
    }
  }

  function onDelete(grackle: GrackleOut) {
    setDeleteConfirm(grackle);
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Content</h1>
          <p>Every recording in {org.name}, regardless of who owns it.</p>
        </div>
      </header>

      {error ? <Notice tone="error">{error}</Notice> : null}
      {rowError ? <Notice tone="error">{rowError}</Notice> : null}

      {loading ? (
        <div className="admin-row-sub">Loading…</div>
      ) : grackles.length === 0 ? (
        <div className="admin-row-sub">No recordings yet.</div>
      ) : (
        <>
          <Card size="flush" className="admin-table-card">
<table className="admin-table">
            <thead>
              <tr>
                <th className="gk-overline">Recording</th>
                <th className="gk-overline">Owner</th>
                <th className="gk-overline">Visibility</th>
                <th className="gk-overline">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {grackles.map((g) => (
                <tr key={g.id}>
                  <td>
                    <Link to={`/g/${g.id}`} className="admin-row-name" style={{ color: "inherit" }}>
                      {g.title || "Untitled"}
                    </Link>
                    {g.on_legal_hold ? (
                      <>
                        {" "}
                        <HoldBadge />
                      </>
                    ) : null}
                    <div className="admin-row-sub">{new Date(g.created_at).toLocaleDateString()}</div>
                  </td>
                  <td>{g.owner.name}</td>
                  <td>
                    <Badge size="sm">{visibilityLabel(g.visibility)}</Badge>
                  </td>
                  <td>
                    <Badge size="sm">{statusLabel(g.status)}</Badge>
                  </td>
                  <td>
                    <div className="row-actions">
                      <ReassignControl grackle={g} members={members} onDone={load} />
                      <Button
                        variant="secondary" size="sm"
                        onClick={() => onDelete(g)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
</Card>
          {cursor ? (
            <Button variant="secondary" onClick={onLoadMore} loading={loadingMore} style={{ alignSelf: "flex-start" }}>
              Load more
            </Button>
          ) : null}
        </>
      )}

      {/* Issue #1: replace window.confirm */}
      {deleteConfirm && (
        <ConfirmDialog
          open={true}
          title="Delete recording"
          message={`Delete "${deleteConfirm.title}"? This can't be undone.`}
          confirmLabel="Delete"
          danger
          onClose={() => setDeleteConfirm(null)}
          onConfirm={onDeleteConfirm}
        />
      )}
    </>
  );
}
